import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { currentLocale } from "@/lib/format/locale";

/**
 * Tijdzone-veilige datum/tijd-weergave.
 *
 * Alles wat een medewerker ziet hoort in de tijdzone van het project te staan, nooit in die van het
 * toestel. Iemand die in Scheveningen op een dienst van 15:00-23:00 staat moet 15:00-23:00 zien,
 * ook als zijn telefoon op `America/Toronto` staat.
 *
 * Dat ging mis: `toLocaleDateString`/`toLocaleTimeString` en tempo's `format()` gebruiken zonder
 * expliciete `timeZone` de zone van het toestel. Op een toestel met een negatieve offset (UTC-4)
 * toonde de app een dienst van 15:00-23:00 als 9:00-17:00, en viel de dagkop boven een project een
 * dag terug — waardoor iemand zich op de verkeerde dag aanmeldde zonder het te kunnen zien.
 *
 * Gebruik daarom altijd deze helpers, nooit `toLocale*` of `format()` direct. De ESLint-regel
 * `no-restricted-syntax` bewaakt dat.
 */

export type DateInput = Date | string | number;

// `timeZone` wordt hier bewust overschreven: `Intl.DateTimeFormatOptions` staat
// geen `null` toe, terwijl de API-velden (`project.timeZone`, `shift.timeZone`)
// nullable zijn. Zonder de Omit moet elke aanroeper zelf een `?? undefined`
// plakken — precies het soort ruis waar de expliciete tijdzone weer uit wegvalt.
type FormatOptions = Omit<Intl.DateTimeFormatOptions, "timeZone"> & {
  /** IANA-zone, standaard `Europe/Amsterdam`. Geef `project.timeZone` mee als je die hebt. */
  timeZone?: string | null;
  /**
   * Standaard de taal die de app nu spreekt (`currentLocale()`). Geef `i18n.language` mee op
   * schermen die vertaald zijn.
   */
  locale?: string;
};

/**
 * De enige plek in de app waar `Intl.DateTimeFormat` rechtstreeks wordt aangeroepen — en dus de
 * enige plek waar de tijdzone nog kan ontbreken. Alles loopt hierlangs zodat er altijd één instaat;
 * de ESLint-regel `no-restricted-syntax` houdt de rest van de codebase hiervandaan.
 */
function dateTimeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string | null,
): Intl.DateTimeFormat {
  // eslint-disable-next-line no-restricted-syntax -- dit ís de helper die de tijdzone forceert
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: timeZone ?? DEFAULT_TIMEZONE });
}

/**
 * Basisformatter. Forceert altijd een tijdzone, ook als de aanroeper er geen meegeeft — dat is
 * precies het gat waar de bug doorheen kwam.
 */
export function formatInTz(value: DateInput, options: FormatOptions = {}): string {
  const { timeZone, locale, ...intlOptions } = options;
  const date = new Date(value);

  // `Intl` gooit op een onbruikbare datum waar `toLocale*` er "Invalid Date" van
  // maakte. Zonder deze regel valt een scherm om op één leeg of kapot veld — en
  // in `toDayKey` neemt dat de hele dag-groepering mee, dus het aanmeldscherm.
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return dateTimeFormat(locale ?? currentLocale(), intlOptions, timeZone).format(date);
}

/**
 * "15:00" — altijd 24-uurs. `hourCycle: "h23"` staat er expliciet zodat een meegegeven `locale` de
 * klok niet stiekem op AM/PM zet.
 */
export function formatTime(value: DateInput, options: FormatOptions = {}): string {
  return formatInTz(value, { hour: "2-digit", minute: "2-digit", hourCycle: "h23", ...options });
}

/** "15:00 - 23:00" */
export function formatTimeRange(
  start: DateInput,
  end: DateInput,
  options: FormatOptions = {},
): string {
  return `${formatTime(start, options)} - ${formatTime(end, options)}`;
}

/** "21 augustus" */
export function formatDayMonth(value: DateInput, options: FormatOptions = {}): string {
  return formatInTz(value, { day: "numeric", month: "long", ...options });
}

/** "vrijdag" */
export function formatWeekday(value: DateInput, options: FormatOptions = {}): string {
  return formatInTz(value, { weekday: "long", ...options });
}

/** "vrijdag 21 augustus 2026" */
export function formatFullDate(value: DateInput, options: FormatOptions = {}): string {
  return formatInTz(value, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    ...options,
  });
}

/**
 * Het datumblokje links op de overzichtskaarten: maand, dag, en daaronder het jaartal (als de datum
 * niet in het lopende jaar valt) of anders de weekdag.
 *
 * Staat hier omdat de aanmeldings-, uitnodigings- en dienstkaart het blokje alle drie identiek
 * opbouwden — en dus ook alle drie dezelfde tijdzonefout maakten.
 */
export function formatDateBox(
  value: DateInput,
  options: FormatOptions = {},
): { month: string; day: string; caption: string } {
  const year = formatInTz(value, { year: "numeric", ...options });
  const isOtherYear = year !== formatInTz(new Date(), { year: "numeric", ...options });

  return {
    month: formatInTz(value, { month: "short", ...options }),
    day: formatInTz(value, { day: "numeric", ...options }),
    caption: isOtherYear ? year : formatInTz(value, { weekday: "short", ...options }),
  };
}

/**
 * Zet een kale dagsleutel ("2026-08-21") om naar een `Date` die in de gegeven tijdzone gegarandeerd
 * op díe dag valt.
 *
 * `new Date("2026-08-21")` levert middernacht **UTC** op. Dat is de tweede helft van de bug: op een
 * toestel met een negatieve offset valt die datum terug naar 20 augustus 20:00, en las de dagkop
 * "donderdag 20 augustus" boven de diensten van vrijdag de 21e.
 *
 * Een vast uur in UTC lost dat niet op. 12:00 UTC houdt stand tot UTC+11, maar in Auckland (UTC+12)
 * of Kiritimati (UTC+14) is dat al de volgende dag — dan verschuift de dagkop precies de andere
 * kant op. Daarom rekent deze functie de echte middernacht in de doeltijdzone uit.
 */
export function dayKeyToDate(dayKey: string, timeZone: string | null = DEFAULT_TIMEZONE): Date {
  const zone = timeZone ?? DEFAULT_TIMEZONE;
  const [year, month, day] = dayKey.split("-").map(Number);
  const naiveMidnight = Date.UTC(year, month - 1, day);

  return new Date(naiveMidnight - tzOffsetMs(new Date(naiveMidnight), zone));
}

/**
 * Dagsleutel ("2026-08-21") van een moment, gerekend in de projecttijdzone. Gebruik dit om diensten
 * per dag te groeperen — niet `date.getDate()`, want dat leest de dag in de tijdzone van het
 * toestel.
 */
export function toDayKey(value: DateInput, timeZone: string | null = DEFAULT_TIMEZONE): string {
  // en-CA levert precies "YYYY-MM-DD".
  return dateTimeFormat(
    "en-CA",
    { year: "numeric", month: "2-digit", day: "2-digit" },
    timeZone,
  ).format(new Date(value));
}

/**
 * Afwijking van een moment ten opzichte van UTC in de gegeven tijdzone, in milliseconden. Positief
 * ten oosten van Greenwich.
 */
function tzOffsetMs(instant: Date, timeZone: string): number {
  const parts = dateTimeFormat(
    "en-CA",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    },
    timeZone,
  ).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  const asIfUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour"),
    read("minute"),
    read("second"),
  );

  return asIfUtc - instant.getTime();
}

/**
 * Het moment waarop een dag begint in de gegeven tijdzone.
 *
 * Nodig omdat "vanaf het begin van vandaag" middernacht in projecttijd betekent, niet middernacht
 * UTC. Met UTC-middernacht viel een dienst die om 00:30 Amsterdamse tijd begint 's nachts twee uur
 * lang buiten het filter.
 *
 * Eén offset-bepaling volstaat. Een tweede pass leek een verbetering voor zones waar de wissel op
 * middernacht valt, maar maakt het juist fout in zones waar middernacht dán niet bestaat (Chili,
 * Cuba, Paraguay): daar landt hij op de vórige kalenderdag. Voor Amsterdam maakt het geen verschil,
 * want de wissel valt hier om 02:00 en niet om 00:00.
 */
export function startOfDayInTz(
  value: DateInput = new Date(),
  timeZone: string | null = DEFAULT_TIMEZONE,
): Date {
  const zone = timeZone ?? DEFAULT_TIMEZONE;
  const [year, month, day] = toDayKey(value, zone).split("-").map(Number);
  const naiveMidnight = Date.UTC(year, month - 1, day);

  return new Date(naiveMidnight - tzOffsetMs(new Date(naiveMidnight), zone));
}
